import { apiRequest } from '../lib/api';

export interface AuthUser {
    id: string;
    email: string;
    full_name?: string;
    role?: string;
    sucursal?: string;
}

export interface SignUpData {
    email: string;
    password: string;
    full_name: string;
}

export interface SignInData {
    email: string;
    password: string;
}

export interface SignInResult {
    user: AuthUser | null;
    error: string | null;
    /** True when the account must define a new password before it can be used. */
    mustResetPassword?: boolean;
    /** Short-lived ticket to pass to forcePasswordChange — only present when mustResetPassword is true. */
    resetTicket?: string;
}

interface LoginResponseBody {
    must_reset_password: boolean;
    reset_ticket?: string;
    user?: AuthUser;
}

export class AuthService {
    /**
     * Sign in with email and password.
     * On success the server sets an HTTP-only session cookie — unless the
     * account still needs to define a new password (mustResetPassword),
     * in which case no cookie is set yet and the caller must complete
     * forcePasswordChange() using the returned resetTicket.
     */
    static async signIn(data: SignInData): Promise<SignInResult> {
        try {
            const res = await apiRequest<LoginResponseBody>('/api/auth/login', {
                method: 'POST',
                body: JSON.stringify(data),
            });
            if (res.must_reset_password) {
                return { user: null, error: null, mustResetPassword: true, resetTicket: res.reset_ticket };
            }
            return { user: res.user ?? null, error: null };
        } catch (error: any) {
            return { user: null, error: error.message || 'Error al iniciar sesión' };
        }
    }

    /** Completes a forced password reset triggered by signIn(). Logs the user in on success. */
    static async forcePasswordChange(resetTicket: string, password: string, confirmPassword: string): Promise<{ user: AuthUser | null; error: string | null }> {
        try {
            const user = await apiRequest<AuthUser>('/api/auth/force-password-change', {
                method: 'POST',
                body: JSON.stringify({ reset_ticket: resetTicket, password, confirm_password: confirmPassword }),
            });
            return { user, error: null };
        } catch (error: any) {
            return { user: null, error: error.message || 'No se pudo actualizar la contraseña' };
        }
    }

    /** Always resolves — the server responds with a generic message either way (anti-enumeration). */
    static async forgotPassword(email: string): Promise<void> {
        await apiRequest('/api/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    static async validateResetToken(token: string): Promise<{ valid: boolean; email?: string }> {
        return apiRequest(`/api/auth/reset-password/${encodeURIComponent(token)}`);
    }

    static async confirmResetPassword(token: string, password: string, confirmPassword: string): Promise<AuthUser> {
        return apiRequest<AuthUser>(`/api/auth/reset-password/${encodeURIComponent(token)}`, {
            method: 'POST',
            body: JSON.stringify({ password, confirm_password: confirmPassword }),
        });
    }

    static async validateInvitation(token: string): Promise<{ valid: boolean; email?: string; full_name?: string }> {
        return apiRequest(`/api/auth/accept-invitation/${encodeURIComponent(token)}`);
    }

    static async confirmAcceptInvitation(token: string, password: string, confirmPassword: string): Promise<AuthUser> {
        return apiRequest<AuthUser>(`/api/auth/accept-invitation/${encodeURIComponent(token)}`, {
            method: 'POST',
            body: JSON.stringify({ password, confirm_password: confirmPassword }),
        });
    }

    /**
     * Sign out — clears the session cookie server-side.
     */
    static async signOut(): Promise<{ error: string | null }> {
        try {
            await apiRequest('/api/auth/logout', { method: 'POST' });
            return { error: null };
        } catch (error: any) {
            return { error: error.message };
        }
    }

    /**
     * Fetch the currently authenticated user from the server.
     * Returns null when there is no active session.
     */
    static async getCurrentUser(): Promise<AuthUser | null> {
        try {
            return await apiRequest<AuthUser>('/api/auth/me');
        } catch {
            return null;
        }
    }

    /**
     * Check session once on app mount.
     * With HTTP-only cookies there is no need for polling —
     * every API call automatically validates the cookie.
     */
    static onAuthStateChange(callback: (user: AuthUser | null) => void) {
        this.getCurrentUser().then(callback);
        return {
            data: {
                subscription: {
                    unsubscribe: () => {},
                },
            },
        };
    }

    /**
     * Update the current user's profile.
     */
    static async updateProfile(
        _userId: string,
        data: {
            full_name?: string;
            sucursal?: string;
            current_password?: string;
            new_password?: string;
        },
    ): Promise<{ user: AuthUser | null; error: string | null }> {
        try {
            const user = await apiRequest<AuthUser>('/api/auth/profile', {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
            return { user, error: null };
        } catch (error: any) {
            return { user: null, error: error.message };
        }
    }

    /**
     * Kept for API compatibility.
     * User registration is handled by the server admin CLI — not the UI.
     */
    static async signUp(
        _data: SignUpData,
    ): Promise<{ user: AuthUser | null; error: string | null }> {
        return {
            user: null,
            error: 'El registro de usuarios se realiza desde el administrador del servidor.',
        };
    }
}
